(() => {
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const labelForWeight = (value) => ({ 400: 'Regular', 600: 'Medium', 700: 'Bold' }[Number(value)] || String(value));

  const initDesigner = (root) => {
    if (!root || root.dataset.ufDesigner552Ready === 'true') return;
    root.dataset.ufDesigner552Ready = 'true';

    const designer = root.querySelector('[data-uf-designer]');
    const textInput = root.querySelector('[data-uf-engraving-text]');
    const fontSelect = root.querySelector('[data-uf-font-select]');
    const customFontInput = root.querySelector('[data-uf-custom-font]');
    const customFontField = root.querySelector('[data-uf-custom-font-field]');
    if (!designer || !textInput || !fontSelect) return;

    const svgText = designer.querySelector('[data-uf-svg-text]');
    const sizeInput = designer.querySelector('[data-uf-font-size]');
    const spacingInput = designer.querySelector('[data-uf-line-spacing]');
    const letterSpacingInput = designer.querySelector('[data-uf-letter-spacing]');
    const sizeOutput = designer.querySelector('[data-uf-font-size-output]');
    const spacingOutput = designer.querySelector('[data-uf-line-spacing-output]');
    const letterSpacingOutput = designer.querySelector('[data-uf-letter-spacing-output]');
    const alignButtons = designer.querySelectorAll('[data-uf-align]');
    const weightButtons = designer.querySelectorAll('[data-uf-weight]');
    const autoFitInput = designer.querySelector('[data-uf-auto-fit]');
    const resetButton = designer.querySelector('[data-uf-designer-reset]');
    const quality = designer.querySelector('[data-uf-quality]');
    const qualityTitle = designer.querySelector('[data-uf-quality-title]');
    const qualityMessage = designer.querySelector('[data-uf-quality-message]');
    const propertySize = designer.querySelector('[data-uf-property-font-size]');
    const propertyAlignment = designer.querySelector('[data-uf-property-alignment]');
    const propertySpacing = designer.querySelector('[data-uf-property-line-spacing]');
    const propertyLetterSpacing = designer.querySelector('[data-uf-property-letter-spacing]');
    const propertyWeight = designer.querySelector('[data-uf-property-font-weight]');
    const propertyAutoFit = designer.querySelector('[data-uf-property-auto-fit]');
    const designJson = designer.querySelector('[data-uf-design-json]');
    const maxLines = Number(designer.dataset.maxLines || 4);
    const minFontSize = Number(designer.dataset.minFontSize || 16);
    const placeholder = designer.dataset.placeholder || 'Your personalization';

    const fontCards = root.querySelectorAll('[data-uf-font-card]');
    const fontSearch = root.querySelector('[data-uf-font-search]');
    const fontCategory = root.querySelector('[data-uf-font-category]');
    const selectedFontLabel = root.querySelector('[data-uf-selected-font]');
    const fontEmpty = root.querySelector('[data-uf-font-empty]');
    const fontDropdown = root.querySelector('[data-uf-font-dropdown]');

    let alignment = 'center';
    let fontWeight = 600;

    const selectedFont = () => {
      if (fontSelect.value === 'Other font requested') return customFontInput?.value.trim() || 'Lora';
      return fontSelect.value || 'Lora';
    };

    const safeFont = (name) => String(name || 'Lora').replace(/["\\]/g, '');
    const fontStack = (name) => {
      const stacks = {
        'Lora': '"Lora", Georgia, serif',
        'Playfair Display': '"Playfair Display", Georgia, serif',
        'Cormorant Garamond': '"Cormorant Garamond", Georgia, serif',
        'Libre Baskerville': '"Libre Baskerville", Georgia, serif',
        'Roboto Slab': '"Roboto Slab", Georgia, serif',
        'Montserrat': '"Montserrat", Arial, sans-serif',
        'Poppins': '"Poppins", Arial, sans-serif',
        'Lato': '"Lato", Arial, sans-serif',
        'Oswald': '"Oswald", Arial, sans-serif',
        'Dancing Script': '"Dancing Script", cursive',
        'Great Vibes': '"Great Vibes", cursive',
        'Alex Brush': '"Alex Brush", cursive',
        'Pacifico': '"Pacifico", cursive',
        'Bebas Neue': '"Bebas Neue", Impact, sans-serif',
        'Roboto Mono': '"Roboto Mono", "Courier New", monospace'
      };
      return stacks[name] || `"${safeFont(name)}", Lora, Georgia, serif`;
    };

    const setFont = (fontName, focusCustom = false) => {
      fontSelect.value = fontName;
      const isCustom = fontName === 'Other font requested';
      if (customFontField) customFontField.hidden = !isCustom;
      if (customFontInput) {
        customFontInput.disabled = !isCustom;
        customFontInput.required = isCustom;
      }
      fontCards.forEach((card) => {
        const active = card.dataset.fontName === fontName;
        card.classList.toggle('is-active', active);
        card.setAttribute('aria-selected', String(active));
      });
      if (selectedFontLabel) selectedFontLabel.textContent = isCustom ? (customFontInput?.value.trim() || 'Custom font') : fontName;
      const appliedName = isCustom ? (customFontInput?.value.trim() || 'Lora') : fontName;
      const stack = fontStack(appliedName);
      if (svgText) svgText.style.fontFamily = stack;
      svgText?.querySelectorAll('tspan').forEach((node) => { node.style.fontFamily = stack; });
      fontSelect.dispatchEvent(new Event('change', { bubbles: true }));
      designer.dispatchEvent(new CustomEvent('uf:font-change', { bubbles: true, detail: { font: appliedName, stack } }));
      if (isCustom && focusCustom) customFontInput?.focus();
    };

    const filterFonts = () => {
      const query = (fontSearch?.value || '').trim().toLowerCase();
      const category = fontCategory?.value || 'all';
      let visible = 0;
      fontCards.forEach((card) => {
        const name = (card.dataset.fontName || '').toLowerCase();
        const cardCategory = card.dataset.fontCategory || '';
        const matchesQuery = !query || name.includes(query);
        const matchesCategory = category === 'all' || cardCategory === category || cardCategory === 'other';
        const show = matchesQuery && matchesCategory;
        card.hidden = !show;
        if (show) visible += 1;
      });
      if (fontEmpty) fontEmpty.hidden = visible > 0;
    };

    const updateQuality = (lines, fontSize, overflowed, autoFit) => {
      const longest = Math.max(0, ...lines.map((line) => line.length));
      let status = 'excellent';
      let title = 'Excellent fit';
      let message = 'Your design fits comfortably inside the engraving area.';

      if (lines.length > maxLines) {
        status = 'error'; title = 'Too many lines'; message = `Use ${maxLines} lines or fewer for this product.`;
      } else if (overflowed && !autoFit) {
        status = 'error'; title = 'Text exceeds the safe area'; message = 'Enable Smart fit, reduce text size or shorten the longest line.';
      } else if (fontSize <= minFontSize + 2 || longest > 42) {
        status = 'warning'; title = 'Design may be crowded'; message = 'Shorten the longest line or increase the text size for clearer engraving.';
      } else if (lines.length >= maxLines || longest > 30) {
        status = 'good'; title = 'Good fit'; message = 'The design fits, but shorter lines may produce a stronger engraving.';
      }

      quality.dataset.status = status;
      qualityTitle.textContent = title;
      qualityMessage.textContent = message;
    };

    const render = () => {
      if (!svgText) return;
      const raw = textInput.value.replace(/\r/g, '');
      const enteredLines = raw.split('\n');
      const visibleLines = (raw.trim() ? enteredLines : [placeholder]).slice(0, Math.max(maxLines, 1));
      const requestedSize = Number(sizeInput?.value || 34);
      const spacing = Number(spacingInput?.value || 1.15);
      const letterSpacing = Number(letterSpacingInput?.value || 0);
      const autoFit = autoFitInput?.checked !== false;
      const longest = Math.max(1, ...visibleLines.map((line) => line.length));
      const approximateWidth = longest * (requestedSize * 0.58 + Math.max(letterSpacing, 0));
      const widthFit = Math.floor(440 / Math.max(longest * 0.58 + Math.max(letterSpacing, 0) / Math.max(requestedSize, 1), 1));
      const fittedSize = autoFit ? clamp(Math.min(requestedSize, widthFit), minFontSize, 72) : requestedSize;
      const overflowed = approximateWidth > 440;
      const totalHeight = fittedSize * spacing * Math.max(visibleLines.length - 1, 0);
      const startY = 180 - totalHeight / 2;
      const x = alignment === 'left' ? 78 : alignment === 'right' ? 522 : 300;
      const anchor = alignment === 'left' ? 'start' : alignment === 'right' ? 'end' : 'middle';
      const font = safeFont(selectedFont());

      svgText.replaceChildren();
      svgText.setAttribute('x', String(x));
      svgText.setAttribute('text-anchor', anchor);
      svgText.setAttribute('font-size', String(fittedSize));
      svgText.setAttribute('font-family', fontStack(font));
      svgText.style.fontFamily = fontStack(font);
      svgText.setAttribute('font-weight', String(fontWeight));
      svgText.setAttribute('letter-spacing', `${letterSpacing}px`);

      visibleLines.forEach((line, index) => {
        const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
        tspan.setAttribute('x', String(x));
        tspan.setAttribute('y', String(startY + index * fittedSize * spacing));
        tspan.textContent = line || ' ';
        svgText.appendChild(tspan);
      });

      if (sizeOutput) sizeOutput.textContent = String(requestedSize);
      if (spacingOutput) spacingOutput.textContent = spacing.toFixed(2);
      if (letterSpacingOutput) letterSpacingOutput.textContent = Number.isInteger(letterSpacing) ? String(letterSpacing) : letterSpacing.toFixed(1);
      if (selectedFontLabel) selectedFontLabel.textContent = fontSelect.value === 'Other font requested' ? (customFontInput?.value.trim() || 'Custom font') : fontSelect.value;
      if (propertySize) propertySize.value = `${requestedSize} px`;
      if (propertyAlignment) propertyAlignment.value = alignment[0].toUpperCase() + alignment.slice(1);
      if (propertySpacing) propertySpacing.value = spacing.toFixed(2);
      if (propertyLetterSpacing) propertyLetterSpacing.value = `${letterSpacing} px`;
      if (propertyWeight) propertyWeight.value = labelForWeight(fontWeight);
      if (propertyAutoFit) propertyAutoFit.value = autoFit ? 'Yes' : 'No';

      const design = {
        version: '5.5.3a', text: raw, lines: enteredLines, font,
        requestedFontSize: requestedSize, renderedFontSize: fittedSize,
        lineSpacing: spacing, letterSpacing, fontWeight,
        alignment, smartFit: autoFit, maxLines
      };
      if (designJson) designJson.value = JSON.stringify(design);
      updateQuality(enteredLines.filter((line) => line.trim().length), fittedSize, overflowed, autoFit);
    };

    fontCards.forEach((card) => card.addEventListener('click', () => {
      const fontName = card.dataset.fontName || 'Georgia';
      setFont(fontName, true);
      render();
      if (fontDropdown && fontName !== 'Other font requested') fontDropdown.open = false;
    }));
    fontSelect.addEventListener('change', render);
    fontSearch?.addEventListener('input', filterFonts);
    fontCategory?.addEventListener('change', filterFonts);
    customFontInput?.addEventListener('input', render);

    [textInput, sizeInput, spacingInput, letterSpacingInput, autoFitInput].forEach((control) => {
      control?.addEventListener('input', render);
      control?.addEventListener('change', render);
    });

    alignButtons.forEach((button) => button.addEventListener('click', () => {
      alignment = button.dataset.ufAlign || 'center';
      alignButtons.forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      render();
    }));

    weightButtons.forEach((button) => button.addEventListener('click', () => {
      fontWeight = Number(button.dataset.ufWeight || 600);
      weightButtons.forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      render();
    }));

    resetButton?.addEventListener('click', () => {
      textInput.value = '';
      if (sizeInput) sizeInput.value = '34';
      if (spacingInput) spacingInput.value = '1.15';
      if (letterSpacingInput) letterSpacingInput.value = '0';
      if (autoFitInput) autoFitInput.checked = true;
      alignment = 'center'; fontWeight = 600;
      setFont('Georgia');
      alignButtons.forEach((item) => {
        const active = item.dataset.ufAlign === 'center';
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      weightButtons.forEach((item) => {
        const active = item.dataset.ufWeight === '600';
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-pressed', String(active));
      });
      if (fontSearch) fontSearch.value = '';
      if (fontCategory) fontCategory.value = 'all';
      filterFonts(); render(); textInput.focus();
      textInput.dispatchEvent(new Event('input', { bubbles: true }));
    });

    setFont(fontSelect.value || 'Georgia');
    filterFonts();
    render();
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-uf-personalization-side]').forEach(initDesigner);
  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
